import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ACCESS_LEVEL_OPTIONS,
  canAccessPath,
  defaultPathForLevel,
  type AccessLevel,
} from "./access-level";
import { useAccessLevel } from "./AccessLevelContext";

export function AccessLevelModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { level, setLevel, dismissPrompt } = useAccessLevel();

  function choose(next: AccessLevel) {
    setLevel(next);
    if (!canAccessPath(next, location.pathname)) {
      void navigate(defaultPathForLevel(next), { replace: true });
    }
  }

  return (
    <div className="nbs-access-backdrop" role="presentation" data-testid="access-level-modal">
      <div
        className="nbs-access-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="access-level-title"
      >
        <p className="nbs-access-modal__eyebrow">{t("access.eyebrow")}</p>
        <h2 id="access-level-title" className="nbs-access-modal__title">
          {t("access.title")}
        </h2>
        <p className="nbs-access-modal__lead">{t("access.lead")}</p>

        <ul className="nbs-access-modal__options">
          {ACCESS_LEVEL_OPTIONS.map((opt) => {
            const current = level === opt.id;
            return (
              <li key={opt.id}>
                <button
                  type="button"
                  className={`nbs-access-modal__option${current ? " nbs-access-modal__option--current" : ""}`}
                  data-testid={`access-level-${opt.id}`}
                  aria-current={current ? "true" : undefined}
                  onClick={() => choose(opt.id)}
                >
                  <span className="nbs-access-modal__badge">
                    {t("access.levelBadge", { level: opt.level })}
                  </span>
                  <span className="nbs-access-modal__option-title">
                    {t(`access.levels.${opt.id}.title`)}
                  </span>
                  <span className="nbs-access-modal__option-desc">
                    {t(`access.levels.${opt.id}.description`)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          className="nbs-access-modal__skip"
          data-testid="access-level-skip"
          onClick={dismissPrompt}
        >
          {level ? t("access.cancel") : t("access.skip")}
        </button>

        <p className="nbs-access-modal__note">{t("access.note")}</p>
      </div>
    </div>
  );
}
